import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

export default function TrainingContentReviewPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canViewTraining } = usePermissions();
  const [sections, setSections] = useState([]);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    if (!canViewTraining) return;
    trainingApi.listSections().then((r) => setSections(r.data)).catch(() => {});
    trainingApi.listSources().then((r) => setSources(r.data)).catch(() => {});
  }, [canViewTraining]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="content-review-access-denied">{t("common.accessDenied")}</p>;

  const sourceName = (id) => sources.find((s) => s.id === id)?.source_id || id;
  const pending = sections.filter((s) => s.management_approval_status === "OPEN" || s.legal_review_status === "OPEN" || s.food_safety_review_status === "OPEN");
  const approved = sections.filter((s) => !pending.includes(s));

  return (
    <div className="space-y-5" data-testid="content-review-page">
      <PageHelpBanner pageKey="training-content-review" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtContentReview.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtContentReview.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtContentReview.subtitle")}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtContentReview.source")}</th><th className="px-4 py-3 font-medium">{t("mgmtContentReview.section")}</th><th className="px-4 py-3 font-medium">{t("mgmtContentReview.legal")}</th><th className="px-4 py-3 font-medium">{t("mgmtContentReview.foodSafety")}</th><th className="px-4 py-3 font-medium">{t("mgmtContentReview.management")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {pending.map((s) => (
              <tr key={s.id} className="border-t border-gray-100" data-testid={`pending-section-row-${s.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{sourceName(s.source_id)}</td>
                <td className="px-4 py-2 text-gray-600">#{s.section_index} - {s.page_or_section_ref}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{s.legal_review_status}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{s.food_safety_review_status}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{s.management_approval_status}</td>
                <td className="px-4 py-2"><button data-testid={`review-section-btn-${s.id}`} onClick={() => navigate(`/management/training/sources/${s.source_id}`)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtContentReview.reviewArrow")}</button></td>
              </tr>
            ))}
            {pending.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-pending-sections-row">{t("mgmtContentReview.noSectionsAwaitingReview")}</td></tr>}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500">{approved.length} {t("mgmtContentReview.sectionsFullyApproved")}</p>
    </div>
  );
}
